import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LogOut, User } from "lucide-react";
import PhoenixImage from "./PhoenixImage";

const linkCls = ({ isActive }) =>
  `text-sm tracking-wide transition-colors ${
    isActive ? "text-teal-300" : "text-neutral-300 hover:text-teal-200"
  }`;

const Nav = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!user) return setIsAdmin(false);
    api.get("/admin/me").then(({ data }) => setIsAdmin(data.is_admin)).catch(() => setIsAdmin(false));
  }, [user]);

  const onLogout = () => {
    logout();
    nav("/");
  };

  return (
    <header className="sticky top-0 z-40 glass">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <PhoenixImage variant="mark" size={40} className="group-hover:opacity-80 transition-opacity" />
          <div className="leading-tight">
            <div className="font-display text-lg tracking-tight text-neutral-100 group-hover:text-teal-100 transition-colors">
              Why We Recover
            </div>
            <div className="text-[9px] tracking-[0.28em] text-teal-300/80 uppercase mt-0.5">
              The pre-therapy standard
            </div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/" end className={linkCls} data-testid="nav-home">Home</NavLink>
          <NavLink to="/story" className={linkCls} data-testid="nav-story">My Story</NavLink>
          <NavLink to="/studio" className={linkCls} data-testid="nav-studio">The Studio</NavLink>
          <NavLink to={user ? "/soundtrack" : "/soundtrack/preview"} className={linkCls} data-testid="nav-soundtrack">Soundtrack</NavLink>
          <NavLink to="/book" className={linkCls} data-testid="nav-book">Book a Session</NavLink>
          {user && (
            <NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard">Dashboard</NavLink>
          )}
          {user && (
            <NavLink to="/journal" className={linkCls} data-testid="nav-journal">Journal</NavLink>
          )}
          {isAdmin && (
            <>
              <NavLink to="/admin/sessions" className={linkCls} data-testid="nav-sessions">Sessions</NavLink>
              <NavLink to="/admin/sales" className={linkCls} data-testid="nav-sales">Sales</NavLink>
            </>
          )}
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <>
              {isAdmin && (
                <>
                  <Link to="/admin/sessions" data-testid="nav-sessions-mobile" className="md:hidden text-xs text-teal-300 hover:text-teal-200">Sessions</Link>
                  <Link to="/admin/sales" data-testid="nav-sales-mobile" className="md:hidden text-xs text-teal-300 hover:text-teal-200">Sales</Link>
                </>
              )}
              <span className="hidden sm:flex items-center gap-2 text-xs text-neutral-400">
                <User size={14} className="text-teal-400" />
                {user.name || user.email}
              </span>
              <button
                onClick={onLogout}
                data-testid="nav-logout-btn"
                className="btn-ghost text-xs px-4 py-2 flex items-center gap-1 whitespace-nowrap"
              >
                <LogOut size={14} /> Log out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-link" className="text-sm text-neutral-300 hover:text-teal-200">
                Log in
              </Link>
              <Link to="/signup" data-testid="nav-signup-btn" className="btn-primary text-sm px-5 py-2">
                Get started
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Nav;
